<template>
    <PreviewModalWrapper>
        <ModalOverlay @click="$emit('close')">
            <ModalContent @click.stop>
                <ModalHeader>
                    <ModalTitle>{{ document.fileName }}</ModalTitle>
                    <CloseButton @click="$emit('close')">&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    <!-- * 로딩 상태 -->
                    <LoadingContainer v-if="isLoading">
                        <LoadingSpinner>미리보기를 불러오는 중...</LoadingSpinner>
                    </LoadingContainer>

                    <!-- * 에러 상태 -->
                    <ErrorContainer v-else-if="error">
                        <ErrorIcon>⚠️</ErrorIcon>
                        <ErrorMessage>{{ error }}</ErrorMessage>
                        <RetryButton @click="loadPreview">다시 시도</RetryButton>
                    </ErrorContainer>

                    <!-- ! 미리보기 컨텐츠 -->
                    <PreviewContainer v-else>
                        <!-- * 이미지 미리보기 -->
                        <img v-if="isImageFile && previewUrl" :src="previewUrl" :alt="document.fileName"
                            @load="onImageLoad" @error="onImageError"
                            style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); display: block; margin: 0 auto;" />

                        <!-- * PDF 미리보기 -->
                        <PDFPreview v-else-if="isPDFFile && previewUrl">
                            <iframe :src="previewUrl" width="100%" height="600px"
                                style="border: none; border-radius: 8px;"></iframe>
                        </PDFPreview>

                        <!-- * 텍스트 파일 미리보기 -->
                        <TextPreview v-else-if="isTextFile">
                            <pre>{{ textContent }}</pre>
                        </TextPreview>

                        <!-- * 지원하지 않는 파일 타입 -->
                        <UnsupportedPreview v-else>
                            <UnsupportedIcon>📁</UnsupportedIcon>
                            <UnsupportedMessage>이 파일 형식은 미리보기를 지원하지 않습니다</UnsupportedMessage>
                            <FileInfo>
                                <InfoItem>
                                    <InfoLabel>파일명:</InfoLabel>
                                    <InfoValue>{{ document.fileName }}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>파일 크기:</InfoLabel>
                                    <InfoValue>{{ formatFileSize(document.size) }}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>파일 형식:</InfoLabel>
                                    <InfoValue>{{ getFileExtension(document.fileName).toUpperCase() }}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>등록일:</InfoLabel>
                                    <InfoValue>{{ formatDate(document.lastModified) }}</InfoValue>
                                </InfoItem>
                            </FileInfo>
                        </UnsupportedPreview>
                    </PreviewContainer>
                </ModalBody>

                <ModalFooter>
                    <DownloadButton @click="downloadDocument">
                        ⬇️ 다운로드
                    </DownloadButton>
                    <CloseModalButton @click="$emit('close')">닫기</CloseModalButton>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    </PreviewModalWrapper>
</template>

<script>
import * as DocumentPreviewModalCss from '@/assets/styles/manage/modal/DocumentPreviewModalCss.js';

export default {
    name: 'DocumentPreviewModal',

    components: {
        ...DocumentPreviewModalCss
    },

    props: {
        document: {
            type: Object,
            required: true
        },
        selectedBeneficiary: {
            type: Object,
            required: true
        }
    },

    emits: ['close'],

    data() {
        return {
            isLoading: true,
            error: null,
            previewUrl: null,
            textContent: null,
            escapeHandler: null,
            debugInfo: null
        }
    },

    computed: {
        fileExtension() {
            return this.getFileExtension(this.document.fileName);
        },

        isImageFile() {
            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(this.fileExtension);
        },

        isPDFFile() {
            return this.fileExtension === 'pdf';
        },

        isTextFile() {
            return ['txt', 'csv', 'json', 'xml', 'log'].includes(this.fileExtension);
        }
    },

    async mounted() {
        // ESC 키로 모달 닫기
        this.escapeHandler = this.handleEscape;
        window.document.addEventListener('keydown', this.escapeHandler);

        await this.loadPreview();
    },

    beforeUnmount() {
        if (this.escapeHandler) {
            window.document.removeEventListener('keydown', this.escapeHandler);
        }

        // 메모리 정리
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.previewUrl);
        }
    },

    methods: {
        async loadPreview() {
            this.isLoading = true;
            this.error = null;
            this.debugInfo = null;

            try {
                // 직접 백엔드 URL을 이미지 src로 사용
                const apiUrl = `${this.$apiUrls.hanqmedi}/documents/${encodeURIComponent(this.document.key)}?beneficiaryName=${encodeURIComponent(this.selectedBeneficiary.name)}`;

                if (this.isImageFile || this.isPDFFile) {
                    // 이미지나 PDF는 직접 URL 사용
                    this.previewUrl = apiUrl;

                    this.debugInfo = {
                        fileName: this.document.fileName,
                        mimeType: this.getMimeType(this.document.fileName),
                        fileSize: this.document.size,
                        directUrl: this.previewUrl,
                        method: 'direct-url'
                    };
                } else if (this.isTextFile) {
                    // 텍스트 파일만 Axios로 가져오기
                    const response = await this.$axios.get(apiUrl, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        responseType: 'text'
                    });

                    this.textContent = response.data;
                } else {
                    // 지원하지 않는 파일 타입
                    this.debugInfo = {
                        fileName: this.document.fileName,
                        mimeType: this.getMimeType(this.document.fileName),
                        fileSize: this.document.size,
                        method: 'unsupported'
                    };
                }

            } catch (error) {
                console.error('미리보기 로드 실패:', error);

                if (error.response?.status === 404) {
                    this.error = '파일을 찾을 수 없습니다.';
                } else if (error.response?.status === 403) {
                    this.error = '파일에 접근할 권한이 없습니다.';
                } else {
                    this.error = '미리보기를 불러올 수 없습니다.';
                }
            } finally {
                this.isLoading = false;
            }
        },

        onImageLoad() {
            console.log('이미지 로드 성공');
        },

        onImageError(event) {
            console.error('이미지 로드 실패:', event);
            console.log('Blob 방식으로 다시 시도합니다...');
        },

        async downloadDocument() {
            try {
                const token = localStorage.getItem('token');
                const downloadUrl = `${this.$apiUrls.hanqmedi}/documents/${encodeURIComponent(this.document.key)}?beneficiaryName=${encodeURIComponent(this.selectedBeneficiary.name)}`;

                const response = await fetch(downloadUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const blob = await response.blob();

                // 파일 다운로드
                const url = window.URL.createObjectURL(blob);
                const link = window.document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.document.fileName);

                // 임시로 DOM에 추가하고 클릭
                window.document.body.appendChild(link);
                link.click();

                // 정리
                window.document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

            } catch (error) {
                console.error('파일 다운로드 실패:', error);

                // 방법 2: 실패 시 직접 링크 다운로드
                try {
                    const downloadUrl = `${this.$apiUrls.hanqmedi}/documents/${encodeURIComponent(this.document.key)}?beneficiaryName=${encodeURIComponent(this.selectedBeneficiary.name)}`;

                    // 새 창에서 열기
                    window.open(downloadUrl, '_blank');

                } catch (linkError) {
                    console.error('직접 링크 다운로드도 실패:', linkError);
                    this.$swal.fire({
                        title: '다운로드 실패',
                        text: '파일 다운로드 중 오류가 발생했습니다.',
                        icon: 'error'
                    });
                }
            }
        },

        getMimeType(fileName) {
            const ext = this.getFileExtension(fileName);
            const mimeTypes = {
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png',
                'gif': 'image/gif',
                'webp': 'image/webp',
                'bmp': 'image/bmp',
                'svg': 'image/svg+xml',
                'pdf': 'application/pdf',
                'txt': 'text/plain',
                'json': 'application/json',
                'xml': 'application/xml',
                'csv': 'text/csv',
                'doc': 'application/msword',
                'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'xls': 'application/vnd.ms-excel',
                'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            };
            return mimeTypes[ext] || 'application/octet-stream';
        },

        handleEscape(e) {
            if (e.key === 'Escape') {
                this.$emit('close');
            }
        },

        getFileExtension(fileName) {
            return fileName.split('.').pop().toLowerCase();
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
}
</script>